/**
 * Humanoid: proportioned low-poly person (~1.8 m) built from primitives and
 * animated procedurally (idle / walk / run / aim / sit / drive / dead / hit / punch).
 *
 * Build strategy: every articulated part is ONE merged BufferGeometry with baked
 * vertex colours, drawn with a single shared MeshStandardMaterial. A humanoid is
 * ~13 draw calls (head, face, neck+torso, pelvis, 4 arm segments, 4 leg segments,
 * optional weapon). No textures except the face (small canvas, cached per skin).
 *
 * Local frame: +Z is forward (heading 0 faces +Z), +Y up, so the character's
 * RIGHT side is -X (right = forward x up).
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import type { HeldWeaponKind, HumanoidAnim, HumanoidStyle, IHumanoid } from './types';
import { clamp, damp, makeRng, pick, TAU } from './util';

// ------------------------------------------------------------------ style ----

export type Hairstyle = 'short' | 'long' | 'bald' | 'bun';

/** Extra (optional) style fields understood by Humanoid on top of the contract's HumanoidStyle. */
export interface HumanoidStyleExtras {
  /** jacket colour: adds an open jacket (second box) + long sleeves */
  jacket?: number;
  hairstyle?: Hairstyle;
  /** sunglasses drawn on the face */
  glasses?: boolean;
  longSleeves?: boolean;
  /** iris colour */
  eyes?: number;
}
export type FullHumanoidStyle = HumanoidStyle & HumanoidStyleExtras;

export const SKIN_TONES = [0xf3d3b3, 0xe9bf9b, 0xdba97f, 0xc98f6a, 0xb47a57, 0x94603f, 0x74482b, 0x5a3720];
export const SHIRT_COLORS = [0xd8433a, 0x2f5fb3, 0x2f8f5b, 0xe6c44a, 0xf1f1ee, 0x1d1d20, 0x8746ad, 0xe57b22, 0x1c9c8c, 0xa5352c, 0x9aa3a8, 0xf0a9bf];
export const PANTS_COLORS = [0x2c3d6b, 0x1f2a45, 0x4d6fa3, 0xb8a27a, 0x8a7a5a, 0x1a1a1c, 0x3c3c40];
export const HAIR_COLORS = [0x1b1411, 0x3a2416, 0x6b4423, 0xa46a3a, 0xd6b06a, 0x2c2c2e, 0x8d8d8d, 0xa63d2c];
const SHOE_COLORS = [0x151515, 0x3a2a1a, 0xe8e8e4, 0x5a4030, 0x243a5a];
const JACKET_COLORS = [0x2a2a2d, 0x5a3b2a, 0x1f3b5a, 0x6b6b6e, 0x3a4a2a, 0x7a2f2f];
const BEANIE_COLORS = [0x2a2a2d, 0x8a2b2b, 0x2f5fb3, 0x6b6b6e, 0x9a7a3a];
const CAP_COLORS = [0x1d1d20, 0xc0392b, 0x2f5fb3, 0xf1f1ee, 0x2f8f5b];
const EYE_COLORS = [0x4a2f1a, 0x3b2a1a, 0x3d6b8f, 0x4c7a4c, 0x6a5a3a];

const POLICE_SHIRT = 0x1e2d5c;
const POLICE_PANTS = 0x141c33;
const POLICE_HAT = 0x18213f;
const MEDIC_SHIRT = 0xf2f4f5;
const MEDIC_PANTS = 0x2e7d50;
const MEDIC_GREEN = 0x2e9e60;
const GOLD = 0xd9b44a;

export function randomStyle(rng: () => number, base: Partial<HumanoidStyle> = {}): HumanoidStyle {
  const r = rng();
  const style: FullHumanoidStyle = {
    skin: pick(rng, SKIN_TONES),
    shirt: pick(rng, SHIRT_COLORS),
    pants: pick(rng, PANTS_COLORS),
    hair: pick(rng, HAIR_COLORS),
    shoes: pick(rng, SHOE_COLORS),
    hat: r < 0.12 ? 'cap' : r < 0.19 ? 'beanie' : 'none',
    uniform: 'none',
    scale: 0.92 + rng() * 0.16,
    jacket: rng() < 0.35 ? pick(rng, JACKET_COLORS) : undefined,
    hairstyle: pick(rng, ['short', 'short', 'short', 'long', 'long', 'bald', 'bun'] as const),
    glasses: rng() < 0.15,
    longSleeves: rng() < 0.5,
    eyes: pick(rng, EYE_COLORS),
    ...base,
  };
  return style;
}

// --------------------------------------------------------- shared resources ----

const BODY_MAT = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.82, metalness: 0.0 });
const GUN_MAT = new THREE.MeshStandardMaterial({ color: 0x25262b, roughness: 0.42, metalness: 0.6 });
const WOOD_MAT = new THREE.MeshStandardMaterial({ color: 0xb58a55, roughness: 0.85, metalness: 0.0 });

const faceMatCache = new Map<string, THREE.MeshStandardMaterial>();
const tmpColor = new THREE.Color();

function hex(c: number): string {
  return '#' + (c & 0xffffff).toString(16).padStart(6, '0');
}

function faceMaterial(skin: number, eyes: number, glasses: boolean): THREE.MeshStandardMaterial {
  const key = `${skin}-${eyes}-${glasses ? 1 : 0}`;
  let m = faceMatCache.get(key);
  if (m) return m;
  const c = document.createElement('canvas');
  c.width = 64;
  c.height = 72;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = hex(skin);
  ctx.fillRect(0, 0, 64, 72);
  // subtle darker shade under the brow / cheeks
  tmpColor.set(skin).multiplyScalar(0.9);
  ctx.fillStyle = `rgba(${Math.round(tmpColor.r * 255)},${Math.round(tmpColor.g * 255)},${Math.round(tmpColor.b * 255)},0.35)`;
  ctx.fillRect(0, 0, 64, 12);
  // eyes
  for (const ex of [22, 42]) {
    ctx.fillStyle = '#f4f1ea';
    ctx.beginPath();
    ctx.ellipse(ex, 31, 6.2, 4.2, 0, 0, TAU);
    ctx.fill();
    ctx.fillStyle = hex(eyes);
    ctx.beginPath();
    ctx.arc(ex, 31.5, 3.1, 0, TAU);
    ctx.fill();
    ctx.fillStyle = '#111';
    ctx.beginPath();
    ctx.arc(ex, 31.5, 1.6, 0, TAU);
    ctx.fill();
    ctx.fillStyle = 'rgba(255,255,255,0.9)';
    ctx.beginPath();
    ctx.arc(ex + 1.1, 30.2, 0.8, 0, TAU);
    ctx.fill();
    // brow
    ctx.strokeStyle = 'rgba(30,20,15,0.75)';
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(ex - 6, 23.5);
    ctx.lineTo(ex + 6, 22.5);
    ctx.stroke();
  }
  // nose
  tmpColor.set(skin).multiplyScalar(0.72);
  ctx.strokeStyle = `rgb(${Math.round(tmpColor.r * 255)},${Math.round(tmpColor.g * 255)},${Math.round(tmpColor.b * 255)})`;
  ctx.lineWidth = 1.6;
  ctx.beginPath();
  ctx.moveTo(32, 34);
  ctx.lineTo(30, 44);
  ctx.lineTo(34, 45);
  ctx.stroke();
  // mouth
  ctx.strokeStyle = 'rgba(110,50,45,0.85)';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(32, 51, 6.5, Math.PI * 0.15, Math.PI * 0.85);
  ctx.stroke();
  if (glasses) {
    ctx.fillStyle = 'rgba(12,12,14,0.96)';
    for (const ex of [22, 42]) {
      ctx.beginPath();
      ctx.roundRect(ex - 8.5, 25, 17, 12, 4);
      ctx.fill();
    }
    ctx.strokeStyle = '#111';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(30, 30);
    ctx.lineTo(34, 30);
    ctx.stroke();
  }
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  tex.generateMipmaps = true;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  m = new THREE.MeshStandardMaterial({ map: tex, roughness: 0.8, metalness: 0 });
  faceMatCache.set(key, m);
  return m;
}

/** Accumulates coloured boxes and merges them into one geometry. */
class PartBuilder {
  private geos: THREE.BufferGeometry[] = [];
  box(w: number, h: number, d: number, x: number, y: number, z: number, color: number, taperTop = 1, taperBottom = 1): this {
    const g = new THREE.BoxGeometry(w, h, d);
    const pos = g.attributes.position as THREE.BufferAttribute;
    if (taperTop !== 1 || taperBottom !== 1) {
      for (let i = 0; i < pos.count; i++) pos.setX(i, pos.getX(i) * (pos.getY(i) > 0 ? taperTop : taperBottom));
      g.computeVertexNormals();
    }
    g.translate(x, y, z);
    tmpColor.set(color);
    const col = new Float32Array(pos.count * 3);
    for (let i = 0; i < pos.count; i++) {
      col[i * 3] = tmpColor.r;
      col[i * 3 + 1] = tmpColor.g;
      col[i * 3 + 2] = tmpColor.b;
    }
    g.setAttribute('color', new THREE.BufferAttribute(col, 3));
    this.geos.push(g);
    return this;
  }
  build(): THREE.BufferGeometry {
    const m = mergeGeometries(this.geos, false)!;
    for (const g of this.geos) g.dispose();
    this.geos = [];
    m.computeBoundingSphere();
    return m;
  }
}

// ----------------------------------------------------------- pose channels ----

const RY = 0, RRX = 1, RRZ = 2;
const HY = 3, HRX = 4, HRY = 5, HRZ = 6;
const TRX = 7, TRY = 8, TRZ = 9;
const HDX = 10, HDY = 11, HDZ = 12;
const SLX = 13, SLY = 14, SLZ = 15, ELL = 16;
const SRX = 17, SRY = 18, SRZ = 19, ELR = 20;
const LLX = 21, LLZ = 22, KNL = 23;
const LRX = 24, LRZ = 25, KNR = 26;
const NCH = 27;

const ANIMS: HumanoidAnim[] = ['idle', 'walk', 'run', 'aim', 'sit', 'dead', 'hit', 'punch', 'drive'];
const ANIM_INDEX: Record<HumanoidAnim, number> = { idle: 0, walk: 1, run: 2, aim: 3, sit: 4, dead: 5, hit: 6, punch: 7, drive: 8 };

interface GaitParams {
  swing: number;
  knee: number;
  kneeBase: number;
  arm: number;
  armBase: number;
  elbowBase: number;
  elbowSwing: number;
  splay: number;
  bob: number;
  lean: number;
  twist: number;
  hipDrop: number;
}
const WALK: GaitParams = { swing: 0.46, knee: 0.6, kneeBase: 0.04, arm: 0.36, armBase: 0.02, elbowBase: 0.18, elbowSwing: 0.35, splay: 0.08, bob: 0.028, lean: 0.05, twist: 0.08, hipDrop: 0.035 };
const RUN: GaitParams = { swing: 0.8, knee: 1.35, kneeBase: 0.12, arm: 0.7, armBase: -0.3, elbowBase: 1.25, elbowSwing: 0.3, splay: 0.14, bob: 0.06, lean: 0.22, twist: 0.11, hipDrop: 0.045 };

const HIP_H = 0.88;
const HIT_DUR = 0.5;
const PUNCH_DUR = 0.42;
const DEAD_FALL = 0.6;

// --------------------------------------------------------------- humanoid ----

export class Humanoid implements IHumanoid {
  group = new THREE.Group();
  style: HumanoidStyle;
  anim: HumanoidAnim = 'idle';

  /** scaled body root (rotated by the dead animation) */
  readonly root = new THREE.Group();
  readonly hips = new THREE.Group();
  readonly torso = new THREE.Group();
  readonly head = new THREE.Group();
  readonly shL = new THREE.Group();
  readonly shR = new THREE.Group();
  readonly elL = new THREE.Group();
  readonly elR = new THREE.Group();
  readonly hipL = new THREE.Group();
  readonly hipR = new THREE.Group();
  readonly kneeL = new THREE.Group();
  readonly kneeR = new THREE.Group();
  /** anchor at the right palm: held weapons are parented here (use for muzzle flashes) */
  readonly rightHand = new THREE.Group();
  readonly extras: HumanoidStyleExtras;
  held: HeldWeaponKind = 'none';
  /** height of the hip pivot above the feet (world units, scale applied) */
  readonly hipHeight: number;
  readonly scale: number;

  private meshes: THREE.Mesh[] = [];
  private weaponMeshes: Partial<Record<HeldWeaponKind, THREE.Object3D>> = {};
  private cur = new Float32Array(NCH);
  private tmp = new Float32Array(NCH);
  private weights = new Float32Array(ANIMS.length);
  private phase = 0;
  private t: number;
  private animT = 0;
  private aimPitch = 0;
  private deadSide = 1;
  private uniform: 'none' | 'police' | 'medic';

  constructor(style: HumanoidStyle) {
    const ex = style as FullHumanoidStyle;
    this.uniform = style.uniform ?? 'none';
    // uniforms enforce their colours for a coherent look
    if (this.uniform === 'police') style = { ...style, shirt: POLICE_SHIRT, pants: POLICE_PANTS, shoes: 0x151515 };
    else if (this.uniform === 'medic') style = { ...style, shirt: MEDIC_SHIRT, pants: MEDIC_PANTS, shoes: 0xe8e8e4 };
    this.style = style;
    // derive hidden variety for styles built by hand (player, mission peds)
    const h = ((style.shirt ^ (style.pants << 3) ^ (style.hair << 7)) >>> 0) % 1000;
    this.extras = {
      jacket: ex.jacket,
      hairstyle: ex.hairstyle ?? (['short', 'short', 'long', 'short', 'bun'] as const)[h % 5],
      glasses: ex.glasses ?? false,
      longSleeves: ex.longSleeves ?? (this.uniform !== 'none' ? true : h % 2 === 0),
      eyes: ex.eyes ?? EYE_COLORS[h % EYE_COLORS.length],
    };
    if (this.uniform !== 'none') this.extras.jacket = undefined;
    this.scale = style.scale ?? 1;
    this.hipHeight = HIP_H * this.scale;
    this.t = (h % 100) * 0.1; // desynchronise idle cycles between peds
    this.weights[0] = 1;
    this.build();
    this.group.add(this.root);
    this.root.scale.setScalar(this.scale);
    this.apply();
  }

  // ---------------------------------------------------------------- build ----

  private mesh(geo: THREE.BufferGeometry, parent: THREE.Object3D, mat: THREE.Material = BODY_MAT): THREE.Mesh {
    const m = new THREE.Mesh(geo, mat);
    m.castShadow = true;
    m.receiveShadow = false;
    parent.add(m);
    this.meshes.push(m);
    return m;
  }

  private build(): void {
    const s = this.style;
    const ex = this.extras;
    const skin = s.skin;
    const shirt = s.shirt;
    const pants = s.pants;
    const shoes = s.shoes ?? 0x151515;
    const sleeve = ex.jacket ?? shirt;
    const forearm = ex.jacket !== undefined || ex.longSleeves ? sleeve : skin;
    const hat = s.hat ?? 'none';

    // hierarchy
    this.root.add(this.hips);
    this.hips.position.y = HIP_H;
    this.hips.rotation.order = 'YXZ';
    this.hips.add(this.torso);
    this.torso.position.y = 0.12;
    this.torso.rotation.order = 'YXZ';
    this.torso.add(this.head);
    this.head.position.y = 0.52;
    this.head.rotation.order = 'YXZ';
    for (const [sh, x] of [
      [this.shL, 0.205],
      [this.shR, -0.205],
    ] as const) {
      sh.position.set(x, 0.42, 0);
      sh.rotation.order = 'YXZ';
      this.torso.add(sh);
    }
    this.elL.position.y = -0.3;
    this.elR.position.y = -0.3;
    this.shL.add(this.elL);
    this.shR.add(this.elR);
    this.rightHand.position.set(0, -0.31, 0);
    this.elR.add(this.rightHand);
    for (const [hp, x] of [
      [this.hipL, 0.095],
      [this.hipR, -0.095],
    ] as const) {
      hp.position.set(x, 0, 0);
      hp.rotation.order = 'YXZ';
      this.hips.add(hp);
    }
    this.kneeL.position.y = -0.42;
    this.kneeR.position.y = -0.42;
    this.hipL.add(this.kneeL);
    this.hipR.add(this.kneeR);

    // pelvis
    const pelvis = new PartBuilder().box(0.34, 0.16, 0.21, 0, 0.04, 0, pants, 1.0, 0.96);
    if (this.uniform === 'police') pelvis.box(0.35, 0.05, 0.225, 0, 0.1, 0, 0x121214).box(0.05, 0.04, 0.012, 0, 0.1, 0.115, GOLD);
    this.mesh(pelvis.build(), this.hips);

    // torso (+ neck, uniform details, jacket)
    const torso = new PartBuilder().box(0.34, 0.46, 0.23, 0, 0.23, 0, shirt, 1.06, 0.94).box(0.11, 0.1, 0.11, 0, 0.48, 0, skin);
    if (this.uniform === 'police') {
      torso
        .box(0.04, 0.05, 0.012, -0.09, 0.32, 0.118, GOLD) // badge (right chest)
        .box(0.385, 0.035, 0.245, 0, 0.17, 0, 0xbfd4f2) // reflective stripe
        .box(0.09, 0.022, 0.07, 0.15, 0.457, 0, 0x3b57a6)
        .box(0.09, 0.022, 0.07, -0.15, 0.457, 0, 0x3b57a6) // epaulettes
        .box(0.03, 0.06, 0.03, 0.12, 0.36, 0.11, 0x111111); // radio
    } else if (this.uniform === 'medic') {
      torso
        .box(0.13, 0.035, 0.012, 0, 0.3, 0.118, MEDIC_GREEN)
        .box(0.035, 0.13, 0.012, 0, 0.3, 0.118, MEDIC_GREEN) // cross
        .box(0.385, 0.05, 0.245, 0, 0.42, 0, MEDIC_GREEN); // shoulder band
    } else if (ex.jacket !== undefined) {
      torso
        .box(0.15, 0.45, 0.255, 0.115, 0.225, 0, ex.jacket, 1.06, 0.94)
        .box(0.15, 0.45, 0.255, -0.115, 0.225, 0, ex.jacket, 1.06, 0.94)
        .box(0.2, 0.045, 0.06, 0, 0.45, -0.075, ex.jacket);
    }
    this.mesh(torso.build(), this.torso);

    // head
    const hd = new PartBuilder().box(0.21, 0.24, 0.21, 0, 0.12, 0, skin).box(0.02, 0.05, 0.04, 0.115, 0.115, 0, skin).box(0.02, 0.05, 0.04, -0.115, 0.115, 0, skin);
    const hair = s.hair;
    const hs = ex.hairstyle ?? 'short';
    const coveredTop = hat !== 'none';
    if (hs !== 'bald') {
      if (!coveredTop) hd.box(0.215, 0.075, 0.225, 0, 0.21, -0.005, hair);
      if (hs === 'long') {
        hd.box(0.215, 0.3, 0.06, 0, 0.02, -0.1, hair).box(0.035, 0.26, 0.18, 0.105, 0.06, -0.02, hair).box(0.035, 0.26, 0.18, -0.105, 0.06, -0.02, hair);
      } else {
        hd.box(0.215, 0.14, 0.05, 0, 0.13, -0.09, hair).box(0.03, 0.09, 0.19, 0.1, 0.165, -0.015, hair).box(0.03, 0.09, 0.19, -0.1, 0.165, -0.015, hair);
        if (hs === 'bun') hd.box(0.085, 0.085, 0.085, 0, 0.2, -0.135, hair);
      }
    } else if (!coveredTop) {
      hd.box(0.215, 0.02, 0.05, 0, 0.13, -0.09, hair); // stubble line
    }
    if (hat === 'cap') {
      const cc = CAP_COLORS[(shirt >>> 4) % CAP_COLORS.length];
      hd.box(0.225, 0.085, 0.23, 0, 0.215, -0.005, cc).box(0.19, 0.018, 0.11, 0, 0.185, 0.16, cc);
    } else if (hat === 'beanie') {
      const bc = BEANIE_COLORS[(pants >>> 3) % BEANIE_COLORS.length];
      hd.box(0.225, 0.13, 0.235, 0, 0.19, -0.005, bc).box(0.235, 0.05, 0.245, 0, 0.145, -0.005, bc);
    } else if (hat === 'police') {
      hd.box(0.235, 0.035, 0.24, 0, 0.18, -0.005, 0x0f1425)
        .box(0.23, 0.07, 0.235, 0, 0.225, -0.005, POLICE_HAT)
        .box(0.25, 0.02, 0.26, 0, 0.265, -0.02, POLICE_HAT)
        .box(0.2, 0.016, 0.1, 0, 0.17, 0.16, 0x0f1425)
        .box(0.035, 0.04, 0.012, 0, 0.215, 0.118, GOLD);
    }
    this.mesh(hd.build(), this.head);
    const face = new THREE.Mesh(new THREE.PlaneGeometry(0.21, 0.24), faceMaterial(skin, ex.eyes ?? EYE_COLORS[0], !!ex.glasses));
    face.position.set(0, 0.12, 0.1055);
    face.castShadow = false;
    this.head.add(face);
    this.meshes.push(face);

    // arms
    for (const [sh, el] of [
      [this.shL, this.elL],
      [this.shR, this.elR],
    ] as const) {
      this.mesh(new PartBuilder().box(0.1, 0.3, 0.1, 0, -0.15, 0, sleeve).box(0.115, 0.06, 0.115, 0, -0.02, 0, sleeve).build(), sh);
      this.mesh(new PartBuilder().box(0.085, 0.27, 0.085, 0, -0.135, 0, forearm).box(0.075, 0.09, 0.085, 0, -0.315, 0, skin).build(), el);
    }
    // legs
    for (const [hp, kn] of [
      [this.hipL, this.kneeL],
      [this.hipR, this.kneeR],
    ] as const) {
      this.mesh(new PartBuilder().box(0.14, 0.42, 0.15, 0, -0.21, 0, pants).build(), hp);
      this.mesh(new PartBuilder().box(0.12, 0.4, 0.13, 0, -0.2, 0, pants).box(0.13, 0.08, 0.27, 0, -0.42, 0.04, shoes).build(), kn);
    }
  }

  private buildWeapon(kind: HeldWeaponKind): THREE.Object3D | null {
    if (kind === 'none') return null;
    const g = new THREE.Group();
    const add = (geo: THREE.BufferGeometry, mat: THREE.Material, x: number, y: number, z: number) => {
      const m = new THREE.Mesh(geo, mat);
      m.position.set(x, y, z);
      m.castShadow = true;
      g.add(m);
      return m;
    };
    // hand local frame: -Y = along the forearm (barrel direction when aiming), -Z = down when aiming.
    if (kind === 'pistol') {
      add(new THREE.BoxGeometry(0.03, 0.17, 0.04), GUN_MAT, 0, -0.1, 0.02);
      add(new THREE.BoxGeometry(0.028, 0.05, 0.09), GUN_MAT, 0, -0.02, -0.03);
      add(new THREE.BoxGeometry(0.012, 0.03, 0.02), GUN_MAT, 0, -0.04, -0.06);
    } else if (kind === 'rifle') {
      add(new THREE.BoxGeometry(0.04, 0.5, 0.055), GUN_MAT, 0, -0.27, 0.02);
      add(new THREE.CylinderGeometry(0.01, 0.01, 0.22, 6), GUN_MAT, 0, -0.58, 0.03);
      add(new THREE.BoxGeometry(0.03, 0.11, 0.05), GUN_MAT, 0, -0.16, -0.04);
      add(new THREE.BoxGeometry(0.035, 0.22, 0.06), WOOD_MAT, 0, 0.1, -0.005);
      add(new THREE.BoxGeometry(0.035, 0.05, 0.04), WOOD_MAT, 0, -0.34, -0.03);
    } else if (kind === 'bat') {
      add(new THREE.CylinderGeometry(0.02, 0.038, 0.72, 8), WOOD_MAT, 0, -0.36, 0);
      add(new THREE.CylinderGeometry(0.03, 0.03, 0.02, 8), WOOD_MAT, 0, 0.02, 0);
    }
    return g;
  }

  // ------------------------------------------------------------------ api ----

  setAnim(a: HumanoidAnim): void {
    if (a === this.anim && a !== 'punch' && a !== 'hit') return;
    if (a === 'dead' && this.anim !== 'dead') this.deadSide = Math.random() < 0.5 ? 1 : -1;
    this.anim = a;
    this.animT = 0;
  }

  setAimPitch(p: number): void {
    this.aimPitch = clamp(p, -0.6, 0.6);
  }

  setHeldWeapon(kind: HeldWeaponKind): void {
    if (kind === this.held) return;
    const prev = this.weaponMeshes[this.held];
    if (prev) prev.visible = false;
    this.held = kind;
    if (kind === 'none') return;
    let w = this.weaponMeshes[kind];
    if (!w) {
      w = this.buildWeapon(kind)!;
      this.weaponMeshes[kind] = w;
      this.rightHand.add(w);
    }
    w.visible = true;
  }

  headPosition(out: THREE.Vector3): THREE.Vector3 {
    this.head.updateWorldMatrix(true, false);
    return out.set(0, 0.135, 0).applyMatrix4(this.head.matrixWorld);
  }

  /** world-space position of the weapon muzzle (or the right fist) */
  muzzlePosition(out: THREE.Vector3): THREE.Vector3 {
    this.rightHand.updateWorldMatrix(true, false);
    const len = this.held === 'rifle' ? 0.68 : this.held === 'pistol' ? 0.2 : this.held === 'bat' ? 0.7 : 0.05;
    return out.set(0, -len, 0.02).applyMatrix4(this.rightHand.matrixWorld);
  }

  dispose(): void {
    for (const m of this.meshes) m.geometry.dispose();
    for (const k of Object.keys(this.weaponMeshes) as HeldWeaponKind[]) {
      this.weaponMeshes[k]?.traverse((o) => {
        const m = o as THREE.Mesh;
        if (m.geometry) m.geometry.dispose();
      });
    }
    this.meshes.length = 0;
    this.group.removeFromParent();
  }

  // --------------------------------------------------------------- update ----

  update(dt: number, speed: number): void {
    if (dt <= 0) return;
    this.t += dt;
    this.animT += dt;
    const a = this.anim;
    const moving = (a === 'walk' || a === 'run') && speed > 0.05;
    if (moving) {
      this.phase += (1.35 + speed * 0.26) * TAU * dt;
      if (this.phase > TAU) this.phase -= TAU;
    }
    // one-shots return to idle on their own
    if ((a === 'hit' && this.animT > HIT_DUR) || (a === 'punch' && this.animT > PUNCH_DUR)) this.anim = 'idle';

    const ai = ANIM_INDEX[this.anim];
    const w = this.weights;
    let sum = 0;
    for (let i = 0; i < w.length; i++) {
      const target = i === ai ? 1 : 0;
      const lam = this.anim === 'dead' ? 9 : i === ANIM_INDEX.punch || ai === ANIM_INDEX.punch ? 22 : 14;
      let v = damp(w[i], target, lam, dt);
      if (target === 0 && v < 0.003) v = 0;
      w[i] = v;
      sum += v;
    }
    const cur = this.cur;
    cur.fill(0);
    const inv = sum > 0 ? 1 / sum : 1;
    for (let i = 0; i < w.length; i++) {
      if (w[i] <= 0) continue;
      this.pose(i, this.tmp);
      const k = w[i] * inv;
      for (let c = 0; c < NCH; c++) cur[c] += this.tmp[c] * k;
    }
    this.apply();
  }

  private pose(i: number, p: Float32Array): void {
    p.fill(0);
    switch (ANIMS[i]) {
      case 'idle':
        this.poseIdle(p);
        break;
      case 'walk':
        this.gait(p, WALK);
        break;
      case 'run':
        this.gait(p, RUN);
        break;
      case 'aim':
        this.poseAim(p);
        break;
      case 'sit':
        this.poseSit(p, false);
        break;
      case 'drive':
        this.poseSit(p, true);
        break;
      case 'dead':
        this.poseDead(p);
        break;
      case 'hit':
        this.poseHit(p);
        break;
      case 'punch':
        this.posePunch(p);
        break;
    }
  }

  private poseIdle(p: Float32Array): void {
    const t = this.t;
    const breath = Math.sin(t * 1.7);
    const sway = Math.sin(t * 0.55);
    p[HY] = 0.004 * breath;
    p[TRX] = 0.02 + 0.012 * breath;
    p[HRZ] = 0.028 * sway;
    p[TRZ] = -0.022 * sway;
    p[HRX] = 0.01;
    p[HDY] = 0.09 * Math.sin(t * 0.37 + 1.3);
    p[HDX] = 0.03 * Math.sin(t * 0.8) - 0.02;
    p[SLX] = 0.06 + 0.02 * breath;
    p[SRX] = 0.06 + 0.02 * breath;
    p[SLZ] = 0.1;
    p[SRZ] = -0.1;
    p[ELL] = -0.16;
    p[ELR] = -0.16;
    p[LLZ] = 0.035 - 0.02 * sway;
    p[LRZ] = -0.035 - 0.02 * sway;
    p[KNL] = 0.03;
    p[KNR] = 0.03;
    if (this.held !== 'none') {
      // keep the weapon clear of the leg
      p[SRZ] = -0.22;
      p[ELR] = -0.35;
    }
  }

  private gait(p: Float32Array, P: GaitParams): void {
    const ph = this.phase;
    const s = Math.sin(ph);
    const c = Math.cos(ph);
    p[LLX] = -P.swing * s;
    p[LRX] = P.swing * s;
    p[KNL] = P.kneeBase + P.knee * Math.max(0, c);
    p[KNR] = P.kneeBase + P.knee * Math.max(0, -c);
    p[SLX] = P.arm * s + P.armBase;
    p[SRX] = -P.arm * s + P.armBase;
    p[ELL] = -(P.elbowBase + P.elbowSwing * Math.max(0, -s));
    p[ELR] = -(P.elbowBase + P.elbowSwing * Math.max(0, s));
    p[SLZ] = P.splay;
    p[SRZ] = -P.splay;
    p[LLZ] = 0.02;
    p[LRZ] = -0.02;
    const c2 = Math.cos(2 * ph);
    p[HY] = -P.bob * 0.5 * (1 - c2);
    p[HRX] = P.lean;
    p[TRX] = P.lean * 0.6;
    p[HRY] = P.twist * s;
    p[TRY] = -P.twist * 1.3 * s;
    p[HRZ] = P.hipDrop * s;
    p[TRZ] = -P.hipDrop * 0.7 * s;
    p[HDX] = -P.lean * 1.1 + 0.01 * c2;
    if (this.held === 'rifle') {
      // carry the rifle across the chest
      p[SRX] = -0.55;
      p[ELR] = -0.9;
      p[SRY] = 0.15;
    }
  }

  private poseAim(p: Float32Array): void {
    const a = this.aimPitch;
    const breath = Math.sin(this.t * 1.7) * 0.01;
    const rifle = this.held === 'rifle';
    const twist = 0.3;
    p[TRY] = twist;
    p[TRX] = 0.06 + breath;
    p[HRY] = 0.08;
    p[HRX] = 0.02;
    p[HDY] = -twist - 0.06;
    p[HDX] = -a * 0.55;
    // right arm: straight along the aim line (torso twist compensated)
    p[SRX] = -Math.PI / 2 - a;
    p[SRY] = -twist + (rifle ? 0.16 : 0.1);
    p[ELR] = rifle ? -0.25 : -0.05;
    // left arm supports the weapon
    p[SLX] = -Math.PI / 2 - a + (rifle ? 0.02 : 0.12);
    p[SLY] = -twist - (rifle ? 0.62 : 0.5);
    p[ELL] = rifle ? -0.3 : -0.55;
    if (this.held === 'bat' || this.held === 'none') {
      // melee guard: fists up
      p[SRX] = -1.2;
      p[SRY] = -twist + 0.25;
      p[ELR] = -1.6;
      p[SLX] = -1.1;
      p[SLY] = -twist - 0.3;
      p[ELL] = -1.75;
      p[HDX] = -a * 0.3;
    }
    p[LLX] = 0.12;
    p[LRX] = -0.14;
    p[LLZ] = 0.09;
    p[LRZ] = -0.09;
    p[KNL] = 0.1;
    p[KNR] = 0.14;
  }

  private poseSit(p: Float32Array, drive: boolean): void {
    p[LLX] = -1.45;
    p[LRX] = -1.45;
    p[LLZ] = 0.1;
    p[LRZ] = -0.1;
    p[KNL] = 1.25;
    p[KNR] = 1.25;
    p[HRX] = -0.12;
    p[TRX] = 0.03;
    p[HDX] = 0.1;
    if (drive) {
      const sway = Math.sin(this.t * 1.7) * 0.05;
      p[SLX] = -1.0 + sway;
      p[SRX] = -1.0 - sway;
      p[SLY] = -0.2;
      p[SRY] = 0.2;
      p[ELL] = -0.45;
      p[ELR] = -0.45;
      p[SLZ] = 0.06;
      p[SRZ] = -0.06;
    } else {
      p[SLX] = -0.5;
      p[SRX] = -0.5;
      p[SLY] = -0.3;
      p[SRY] = 0.3;
      p[ELL] = -0.95;
      p[ELR] = -0.95;
      p[HDY] = 0.15 * Math.sin(this.t * 0.4);
    }
  }

  private poseDead(p: Float32Array): void {
    const u = this.animT;
    const k = clamp(u / DEAD_FALL, 0, 1);
    const e = 1 - (1 - k) * (1 - k) * (1 - k);
    const side = this.deadSide;
    let roll = side * 1.56 * e;
    if (k >= 1 && u < 1.4) {
      const s = u - DEAD_FALL;
      roll += side * Math.sin(s * 16) * Math.exp(-s * 6) * 0.05;
    }
    p[RRZ] = roll;
    p[RRX] = -0.1 * e;
    p[RY] = 0.2 * e;
    p[HRX] = 0.12 * e;
    p[TRX] = 0.14 * e;
    p[TRZ] = side * 0.05 * e;
    p[HDX] = -0.25 * e;
    p[HDY] = side * 0.4 * e;
    // arms: the one on the ground side swings forward, the other flops over the body
    const groundIsRight = side > 0;
    p[SRX] = (groundIsRight ? -0.75 : 0.35) * e;
    p[SLX] = (groundIsRight ? -0.5 : -0.75) * e;
    p[SRZ] = (groundIsRight ? -0.1 : -0.35) * e;
    p[SLZ] = (groundIsRight ? 0.35 : 0.1) * e;
    p[ELR] = -0.35 * e;
    p[ELL] = -0.45 * e;
    p[LLX] = -0.35 * e;
    p[LRX] = 0.05 * e;
    p[LLZ] = 0.05 * e;
    p[KNL] = 0.55 * e;
    p[KNR] = 0.2 * e;
  }

  private poseHit(p: Float32Array): void {
    const k = clamp(this.animT / HIT_DUR, 0, 1);
    const env = Math.sin(k * Math.PI);
    p[HRX] = -0.32 * env;
    p[TRX] = -0.16 * env;
    p[HDX] = -0.3 * env;
    p[HY] = -0.03 * env;
    p[SLX] = -0.7 * env;
    p[SRX] = -0.95 * env;
    p[SLZ] = 0.35 * env + 0.08;
    p[SRZ] = -0.35 * env - 0.08;
    p[ELL] = -0.6 * env - 0.15;
    p[ELR] = -0.5 * env - 0.15;
    p[LLX] = 0.2 * env;
    p[LRX] = -0.12 * env;
    p[KNR] = 0.35 * env;
    p[KNL] = 0.1 * env;
  }

  private posePunch(p: Float32Array): void {
    const k = clamp(this.animT / PUNCH_DUR, 0, 1);
    const jab = k < 0.4 ? Math.sin((k / 0.4) * Math.PI * 0.5) : Math.cos(((k - 0.4) / 0.6) * Math.PI * 0.5);
    p[SRX] = -0.4 - 1.15 * jab;
    p[SRY] = 0.28 * jab;
    p[SRZ] = -0.05;
    p[ELR] = -1.45 * (1 - jab) - 0.08;
    p[SLX] = -0.75;
    p[SLY] = -0.25;
    p[SLZ] = 0.12;
    p[ELL] = -1.8;
    p[TRY] = 0.45 * jab;
    p[HRY] = 0.15 * jab;
    p[HRX] = 0.1 * jab + 0.05;
    p[HDY] = -0.4 * jab;
    p[LLX] = -0.18;
    p[LRX] = 0.18;
    p[LLZ] = 0.06;
    p[LRZ] = -0.06;
    p[KNL] = 0.18;
    p[KNR] = 0.15;
  }

  private apply(): void {
    const c = this.cur;
    this.root.position.y = c[RY] * this.scale;
    this.root.rotation.set(c[RRX], 0, c[RRZ]);
    this.hips.position.y = HIP_H + c[HY];
    this.hips.rotation.set(c[HRX], c[HRY], c[HRZ]);
    this.torso.rotation.set(c[TRX], c[TRY], c[TRZ]);
    this.head.rotation.set(c[HDX], c[HDY], c[HDZ]);
    this.shL.rotation.set(c[SLX], c[SLY], c[SLZ]);
    this.shR.rotation.set(c[SRX], c[SRY], c[SRZ]);
    this.elL.rotation.x = c[ELL];
    this.elR.rotation.x = c[ELR];
    this.hipL.rotation.set(c[LLX], 0, c[LLZ]);
    this.hipR.rotation.set(c[LRX], 0, c[LRZ]);
    this.kneeL.rotation.x = c[KNL];
    this.kneeR.rotation.x = c[KNR];
  }
}

export const defaultRng = makeRng(7);
